/**
 * Internal shared types + helpers for the SpreadsheetConnectDialog
 * shell and its two extracted step components. NOT re-exported through
 * the connectors barrel — the public API surface of the spreadsheet
 * dialog stays the same as before the refactor (only the dialog itself
 * + its props + ``SpreadsheetSourceMode`` are exported).
 */

import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type * as React from "react";

import type { DriveProvider } from "@ando/core";

import { SPREADSHEET_MAX_UPLOAD_BYTES } from "../../wizards/spreadsheetSpec";
import type { SpreadsheetSourceMode } from "../../wizards/spreadsheetSpec";

// ---------------------------------------------------------------------------
// Public picker contract — re-exported from ``SpreadsheetConnectDialog``
// for backwards compatibility with existing callers. Lives here so the
// orchestration hook + the dialog can both depend on it without forming
// a circular import.
// ---------------------------------------------------------------------------

export interface PickerFileSelection {
  readonly id: string;
  readonly name: string;
  readonly mime_type?: string;
  readonly size_bytes?: number;
  readonly is_folder: boolean;
}

export interface PickerOpenInput {
  readonly provider: DriveProvider;
  readonly connectionKey?: string;
  readonly allowFiles?: boolean;
  readonly allowFolders?: boolean;
  readonly spreadsheetOnly?: boolean;
}

export interface PickerOpenOutcome {
  readonly cancelled: boolean;
  readonly items: readonly PickerFileSelection[];
}

export type PickerOpener = (opts: PickerOpenInput) => Promise<PickerOpenOutcome>;

// ---------------------------------------------------------------------------
// Internal phase / selection shapes
// ---------------------------------------------------------------------------

export type Phase = "idle" | "submitting";

export interface CloudFileSelection {
  readonly id: string;
  readonly name: string;
  readonly mime?: string;
  readonly size_bytes?: number;
  readonly provider: "google_drive" | "onedrive";
  readonly connectionKey?: string;
}

/** Discriminator-narrowed input shape passed to ``usePreviewSpreadsheet``. */
export type PreviewInput =
  | { readonly kind: "upload"; readonly file: File }
  | {
      readonly kind: "cloud";
      readonly source_file_id: string;
      readonly source_provider: "google_drive" | "onedrive";
      readonly source_connection_key?: string;
    };

// ---------------------------------------------------------------------------
// Copy / labels
// ---------------------------------------------------------------------------

export const SOURCE_LABELS: Readonly<Record<SpreadsheetSourceMode, string>> = {
  // `upload` is the only real copy here — accessed live via a getter so it
  // resolves against the active locale at render time (brand names below are
  // locale-invariant literals).
  get upload(): string {
    return i18nSingleton._(msg`Carica file`);
  },
  google_drive: "Google Drive",
  onedrive: "OneDrive",
};

export const SOURCE_PROVIDER_LABEL: Readonly<
  Record<Exclude<SpreadsheetSourceMode, "upload">, string>
> = {
  google_drive: "Google Drive",
  onedrive: "OneDrive",
};

// ---------------------------------------------------------------------------
// Format / validation helpers
// ---------------------------------------------------------------------------

export function fileBaseName(name: string): string {
  // Strip extension for the auto-suggested connector_name.
  const idx = name.lastIndexOf(".");
  return idx > 0 ? name.slice(0, idx) : name;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toLocaleString("it-IT", { maximumFractionDigits: 1 })} KB`;
  const mb = kb / 1024;
  return `${mb.toLocaleString("it-IT", { maximumFractionDigits: 2 })} MB`;
}

export function humanizeFileError(file: File): string | null {
  if (file.size > SPREADSHEET_MAX_UPLOAD_BYTES) {
    const maxMb = Math.round(SPREADSHEET_MAX_UPLOAD_BYTES / 1024 / 1024);
    return i18nSingleton._(msg`Il file supera i ${maxMb} MB. Riduci le righe o suddividilo.`);
  }
  if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
    return i18nSingleton._(msg`Formato non supportato. Usa .xlsx, .xls o .csv.`);
  }
  return null;
}

/**
 * Best-effort stable key for a preview-row used as the ``key`` prop of
 * a ``<tr>``. Falls back to ``rowFallback`` (typically ``row-<idx>``)
 * when ``JSON.stringify`` can't serialise the row (e.g. a BigInt cell).
 * The data is immutable for the lifetime of Step 2 so the index would
 * be safe, but Biome's ``noArrayIndexKey`` doesn't know that — using
 * the content hash sidesteps the lint without compromising reordering
 * stability for the (currently impossible) case where rows shift.
 */
export function stableKey(value: unknown, rowFallback: string): string {
  try {
    return JSON.stringify(value);
  } catch {
    return rowFallback;
  }
}

/**
 * Pretty-print a primitive cell from the preview rows. Nulls render as
 * an italic "vuoto"; booleans as Sì/No; dates pass-through as strings
 * (the BE already serialises them to ISO-8601). Objects fall back to
 * ``JSON.stringify`` so we never crash on an unexpected shape.
 */
export function renderCell(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-ink-3/60 italic">{i18nSingleton._(msg`vuoto`)}</span>;
  }
  if (typeof value === "boolean") return value ? i18nSingleton._(msg`Sì`) : i18nSingleton._(msg`No`);
  if (typeof value === "number" || typeof value === "string") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "—";
  }
}
